import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import {
  getEmployeeRoles,
  createEmployeeRole,
  updateEmployeeRole,
  deleteEmployeeRole,
  getRopTeam,
  updateRopTeam,
  getAvailableOperators,
  getCommissionSettings,
  updateCommissionSetting,
  getUsers,
  getSalaryReport,
} from '../api';

interface EmployeeRole {
  id: number;
  user_id: number;
  role_type: 'OPERATOR' | 'ROP' | 'MOP';
  base_salary: number;
  user_name?: string;
  user_email?: string;
  operator_name?: string;
}

interface CommissionSetting {
  id: number;
  role_type: string;
  commission_type: string;
  percentage: number | null;
  fixed_amount: number | null;
  threshold: number | null;
}

interface User {
  id: number;
  name: string;
  email: string;
  operator_name?: string;
}

const roleLabels: Record<string, string> = {
  OPERATOR: 'Оператор',
  ROP: 'РОП',
  MOP: 'МОП',
};

const commissionLabels: Record<string, string> = {
  TEAM_SALES: 'Комиссия от продаж команды',
  PERSONAL_SALES: 'Комиссия от личных продаж',
  PROJECT_PROFIT: 'Комиссия от прибыли проектов',
  PER_LEAD: 'Бонус за лид (базовый)',
  PER_LEAD_BONUS: 'Бонус за лид (повышенный)',
};

const currencyFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
});

export const EmployeeRolesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [roles, setRoles] = useState<EmployeeRole[]>([]);
  const [commissions, setCommissions] = useState<CommissionSetting[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<EmployeeRole | null>(null);
  const [newRole, setNewRole] = useState({
    userId: 0,
    roleType: 'OPERATOR' as 'OPERATOR' | 'ROP' | 'MOP',
    baseSalary: 0,
  });

  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [selectedRop, setSelectedRop] = useState<EmployeeRole | null>(null);
  const [teamMembers, setTeamMembers] = useState<number[]>([]);
  const [availableOperators, setAvailableOperators] = useState<any[]>([]);

  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [editingCommission, setEditingCommission] = useState<CommissionSetting | null>(null);

  // Salary report
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [salaryReport, setSalaryReport] = useState<any>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesData, commissionsData, usersData] = await Promise.all([
        getEmployeeRoles(),
        getCommissionSettings(),
        getUsers(),
      ]);
      setRoles(rolesData);
      setCommissions(commissionsData);
      setUsers(usersData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSalaryReport = useCallback(async () => {
    try {
      const data = await getSalaryReport(month);
      setSalaryReport(data);
    } catch (err) {
      // Ignore errors for salary report
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 2) {
      fetchSalaryReport();
    }
  }, [activeTab, month, fetchSalaryReport]);

  // Role CRUD
  const handleCreateRole = async () => {
    try {
      await createEmployeeRole(newRole);
      setRoleDialogOpen(false);
      setNewRole({ userId: 0, roleType: 'OPERATOR', baseSalary: 0 });
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to create role');
    }
  };

  const handleUpdateRole = async () => {
    if (!editingRole) return;
    try {
      await updateEmployeeRole(editingRole.id, {
        roleType: editingRole.role_type,
        baseSalary: editingRole.base_salary,
      });
      setEditingRole(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to update role');
    }
  };

  const handleDeleteRole = async (id: number) => {
    if (!window.confirm('Удалить роль сотрудника?')) return;
    try {
      await deleteEmployeeRole(id);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete role');
    }
  };

  // Team management
  const handleOpenTeamDialog = async (rop: EmployeeRole) => {
    setSelectedRop(rop);
    try {
      const [team, operators] = await Promise.all([
        getRopTeam(rop.user_id),
        getAvailableOperators(rop.user_id),
      ]);
      setTeamMembers(team.map((t: any) => t.operator_user_id));
      setAvailableOperators(operators);
      setTeamDialogOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load team');
    }
  };

  const handleUpdateTeam = async () => {
    if (!selectedRop) return;
    try {
      await updateRopTeam(selectedRop.user_id, teamMembers);
      setTeamDialogOpen(false);
      setSelectedRop(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update team');
    }
  };

  // Commission settings
  const handleUpdateCommission = async () => {
    if (!editingCommission) return;
    try {
      await updateCommissionSetting(editingCommission.id, {
        percentage: editingCommission.percentage,
        fixedAmount: editingCommission.fixed_amount,
        threshold: editingCommission.threshold,
      });
      setEditingCommission(null);
      setCommissionDialogOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to update commission');
    }
  };

  const usersWithoutRole = users.filter(
    (u) => !roles.some((r) => r.user_id === u.id)
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Управление сотрудниками</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Роли сотрудников" />
        <Tab label="Настройки комиссий" />
        <Tab label="Отчёт по ЗП" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Tab 0: Employee Roles */}
          {activeTab === 0 && (
            <>
              <Box sx={{ mb: 2 }}>
                <Button variant="contained" onClick={() => setRoleDialogOpen(true)}>
                  Добавить роль
                </Button>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Сотрудник</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Роль</TableCell>
                      <TableCell align="right">Базовый оклад</TableCell>
                      <TableCell align="center">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell>{role.user_name || role.operator_name || '—'}</TableCell>
                        <TableCell>{role.user_email}</TableCell>
                        <TableCell>
                          <Chip
                            label={roleLabels[role.role_type]}
                            color={
                              role.role_type === 'ROP' ? 'primary' :
                              role.role_type === 'MOP' ? 'secondary' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {currencyFormatter.format(role.base_salary)}
                        </TableCell>
                        <TableCell align="center">
                          {role.role_type === 'ROP' && (
                            <IconButton size="small" onClick={() => handleOpenTeamDialog(role)} title="Команда">
                              <GroupIcon />
                            </IconButton>
                          )}
                          <IconButton size="small" onClick={() => setEditingRole(role)} title="Редактировать">
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteRole(role.id)} title="Удалить">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {roles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">Нет назначенных ролей</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Tab 1: Commission Settings */}
          {activeTab === 1 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Роль</TableCell>
                    <TableCell>Тип комиссии</TableCell>
                    <TableCell align="right">Процент</TableCell>
                    <TableCell align="right">Фикс. сумма</TableCell>
                    <TableCell align="right">Порог</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commissions.map((comm) => (
                    <TableRow key={comm.id}>
                      <TableCell>{roleLabels[comm.role_type] || comm.role_type}</TableCell>
                      <TableCell>{commissionLabels[comm.commission_type] || comm.commission_type}</TableCell>
                      <TableCell align="right">
                        {comm.percentage != null ? `${comm.percentage}%` : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {comm.fixed_amount != null ? currencyFormatter.format(comm.fixed_amount) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {comm.threshold != null ? comm.threshold : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingCommission(comm);
                            setCommissionDialogOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Tab 2: Salary Report */}
          {activeTab === 2 && (
            <>
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Период"
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  size="small"
                />
              </Box>

              {salaryReport && (
                <>
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>IQTech</Typography>
                          <Typography variant="h6">{currencyFormatter.format(salaryReport.iqtechCost)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>ЗП РОП</Typography>
                          <Typography variant="h6">{currencyFormatter.format(salaryReport.totalRopSalary)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>ЗП МОП</Typography>
                          <Typography variant="h6">{currencyFormatter.format(salaryReport.totalMopSalary)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>Всего ЗП</Typography>
                          <Typography variant="h6">{currencyFormatter.format(salaryReport.totalSalary)}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {salaryReport.ropSalaries.length > 0 && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2 }}>Расчёт ЗП РОП</Typography>
                      <TableContainer component={Paper} sx={{ mb: 4 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>РОП</TableCell>
                              <TableCell align="right">Базовый оклад</TableCell>
                              <TableCell align="right">Продажи команды</TableCell>
                              <TableCell align="right">Комиссия команды</TableCell>
                              <TableCell align="right">Личные продажи</TableCell>
                              <TableCell align="right">Личная комиссия</TableCell>
                              <TableCell align="right">Итого</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {salaryReport.ropSalaries.map((rop: any) => (
                              <TableRow key={rop.userId}>
                                <TableCell>ID: {rop.userId}</TableCell>
                                <TableCell align="right">{currencyFormatter.format(rop.baseSalary)}</TableCell>
                                <TableCell align="right">{currencyFormatter.format(rop.teamSales)}</TableCell>
                                <TableCell align="right">
                                  {currencyFormatter.format(rop.teamCommission)} ({rop.teamCommissionRate}%)
                                </TableCell>
                                <TableCell align="right">{currencyFormatter.format(rop.personalSales)}</TableCell>
                                <TableCell align="right">
                                  {currencyFormatter.format(rop.personalCommission)} ({rop.personalCommissionRate}%)
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {currencyFormatter.format(rop.totalSalary)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}

                  {salaryReport.mopSalaries.length > 0 && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2 }}>Расчёт ЗП МОП</Typography>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>МОП</TableCell>
                              <TableCell align="right">Базовый оклад</TableCell>
                              <TableCell align="right">Общая прибыль проектов</TableCell>
                              <TableCell align="right">Комиссия от прибыли</TableCell>
                              <TableCell align="right">Итого</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {salaryReport.mopSalaries.map((mop: any) => (
                              <TableRow key={mop.userId}>
                                <TableCell>ID: {mop.userId}</TableCell>
                                <TableCell align="right">{currencyFormatter.format(mop.baseSalary)}</TableCell>
                                <TableCell align="right">{currencyFormatter.format(mop.totalProjectProfit)}</TableCell>
                                <TableCell align="right">
                                  {currencyFormatter.format(mop.profitCommission)} ({mop.profitCommissionRate}% / {mop.mopCount} МОП)
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {currencyFormatter.format(mop.totalSalary)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Create Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить роль сотруднику</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Сотрудник</InputLabel>
              <Select
                value={newRole.userId}
                label="Сотрудник"
                onChange={(e) => setNewRole({ ...newRole, userId: e.target.value as number })}
              >
                {usersWithoutRole.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name || user.email} ({user.operator_name || 'нет имени оператора'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Роль</InputLabel>
              <Select
                value={newRole.roleType}
                label="Роль"
                onChange={(e) => setNewRole({ ...newRole, roleType: e.target.value as any })}
              >
                <MenuItem value="OPERATOR">Оператор</MenuItem>
                <MenuItem value="ROP">РОП</MenuItem>
                <MenuItem value="MOP">МОП</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Базовый оклад"
              type="number"
              value={newRole.baseSalary}
              onChange={(e) => setNewRole({ ...newRole, baseSalary: Number(e.target.value) })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleCreateRole} variant="contained" disabled={!newRole.userId}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editingRole} onClose={() => setEditingRole(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Редактировать роль</DialogTitle>
        <DialogContent>
          {editingRole && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography>
                Сотрудник: {editingRole.user_name || editingRole.operator_name}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Роль</InputLabel>
                <Select
                  value={editingRole.role_type}
                  label="Роль"
                  onChange={(e) => setEditingRole({ ...editingRole, role_type: e.target.value as any })}
                >
                  <MenuItem value="OPERATOR">Оператор</MenuItem>
                  <MenuItem value="ROP">РОП</MenuItem>
                  <MenuItem value="MOP">МОП</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Базовый оклад"
                type="number"
                value={editingRole.base_salary}
                onChange={(e) => setEditingRole({ ...editingRole, base_salary: Number(e.target.value) })}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingRole(null)}>Отмена</Button>
          <Button onClick={handleUpdateRole} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>

      {/* Team Dialog */}
      <Dialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Команда РОП: {selectedRop?.user_name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Выберите операторов, входящих в команду РОП
            </Typography>
            {availableOperators.map((op) => (
              <FormControlLabel
                key={op.id}
                control={
                  <Checkbox
                    checked={teamMembers.includes(op.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setTeamMembers([...teamMembers, op.id]);
                      } else {
                        setTeamMembers(teamMembers.filter((id) => id !== op.id));
                      }
                    }}
                  />
                }
                label={`${op.name || op.operator_name || 'Без имени'} (${op.email})`}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleUpdateTeam} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>

      {/* Commission Dialog */}
      <Dialog open={commissionDialogOpen} onClose={() => setCommissionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Редактировать комиссию</DialogTitle>
        <DialogContent>
          {editingCommission && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography>
                {roleLabels[editingCommission.role_type]} - {commissionLabels[editingCommission.commission_type]}
              </Typography>
              {editingCommission.percentage !== null && (
                <TextField
                  label="Процент"
                  type="number"
                  value={editingCommission.percentage || ''}
                  onChange={(e) => setEditingCommission({
                    ...editingCommission,
                    percentage: e.target.value ? Number(e.target.value) : null,
                  })}
                  fullWidth
                  InputProps={{ endAdornment: '%' }}
                />
              )}
              {editingCommission.fixed_amount !== null && (
                <TextField
                  label="Фиксированная сумма"
                  type="number"
                  value={editingCommission.fixed_amount || ''}
                  onChange={(e) => setEditingCommission({
                    ...editingCommission,
                    fixed_amount: e.target.value ? Number(e.target.value) : null,
                  })}
                  fullWidth
                />
              )}
              {editingCommission.commission_type === 'PER_LEAD_BONUS' && (
                <TextField
                  label="Порог (количество лидов)"
                  type="number"
                  value={editingCommission.threshold || ''}
                  onChange={(e) => setEditingCommission({
                    ...editingCommission,
                    threshold: e.target.value ? Number(e.target.value) : null,
                  })}
                  fullWidth
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleUpdateCommission} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeRolesPage;
